import type {AddSubtitleData, AddWordData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {GeneratedFromType, TranscriptGeneratedFrom, TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {MIN_TIME_GAP_FOR_TRANSCRIPT} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {AiTranscriptSupportedLanguage} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {AiTranscriptForItemWithParams} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {WordObject} from '@PosterWhiteboard/items/transcript-item/subtitle/word/word.types';
import {getUniqueString, removePunctuationFromWord} from '@Utils/string.util';
import type {SelectableItemWithAudio} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {repoVideoThumbImageURL} from '@Libraries/user-video-library';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from '@Components/add-ai-item-button/add-ai-item-button.module.scss';
import type {PosterEditorSubtitles} from '@Components/poster-editor/components/poster-editor-subtitles-editor/poster-editor-subtitles-editor.types';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import {getTrimmedDuration, getTrimmedEndTime, getTrimmedPlaybackDuration, getTrimmedStartTime} from '@Libraries/audio-player/audio-player.library';

export const NO_SPEECH_DETECTED_ERROR_CODE = 'NO_SPEECH_DETECTED';

export enum AiSubtitleMenuOpenedFrom {
  ADD_AI_OPTION = 'add_ai_option',
  ADD_TEXT_OPTION = 'add_text_option',
}

export interface GeneratedTranscriptWordsData {
  id: string;
  start: number;
  end: number;
  text: string;
}

export interface GeneratedTranscriptSubtitlesData {
  id: string;
  start: number;
  end: number;
  text: string;
  words: GeneratedTranscriptWordsData[];
}

export interface GeneratedAiTranscriptData {
  text: string;
  subtitles: GeneratedTranscriptSubtitlesData[];
}

export interface AiTranscriptGenerationResponse {
  transcript?: GeneratedAiTranscriptData;
  error?: string;
}

export const doesTranscriptForItemNeedRegeneration = (params: AiTranscriptForItemWithParams, existingTranscriptItems: TranscriptItemObject[]): boolean => {
  return (
    !doTrimTimesMatchGeneratedTranscriptForItems(params.startTime, params.endTime, params.onPageStartTime, params.onPageEndTime, existingTranscriptItems) ||
    doesAnyTranscriptHaveUserEditedSubtitles(existingTranscriptItems) ||
    hasSpeedBeenChangedAfterAiTranscriptGenerationForItems(params.speed, existingTranscriptItems)
  );
};

export const doTrimTimesMatchGeneratedTranscriptForItems = (
  trimStartTime: number,
  trimEndTime: number,
  onPageStartTime: number,
  onPageEndTime: number,
  generatedTranscripts: TranscriptItemObject[]
): boolean => {
  if (!generatedTranscripts.length) {
    return false;
  }

  return generatedTranscripts.every((transcript) => doTrimTimesMatchGeneratedTranscript(trimStartTime, trimEndTime, onPageStartTime, onPageEndTime, transcript));
};

export const doTrimTimesMatchGeneratedTranscript = (
  trimStartTime: number,
  trimEndTime: number,
  onPageStartTime: number,
  onPageEndTime: number,
  generatedTranscript: TranscriptItemObject
): boolean => {
  if (!generatedTranscript.generatedFrom) {
    return false;
  }
  const generatedFromStartTime = generatedTranscript.generatedFrom.startTime;
  const generatedFromEndTime = generatedTranscript.generatedFrom.endTime;
  const generatedFromOnPageStartTime = generatedTranscript.generatedFrom.onPageStartTime;
  const generatedFromOnPageEndTime = generatedTranscript.generatedFrom.onPageEndTime;

  return (
    trimStartTime === generatedFromStartTime &&
    trimEndTime === generatedFromEndTime &&
    onPageStartTime === generatedFromOnPageStartTime &&
    onPageEndTime === generatedFromOnPageEndTime
  );
};

export const doesAnyTranscriptHaveUserEditedSubtitles = (transcripts: TranscriptItemObject[]): boolean => {
  return transcripts.some(doesTranscriptHaveUserEditedSubtitles);
};

export const generateAiTranscriptAndGetSubtitles = async (
  audioHashedFilename: string,
  trimStartTime: number,
  trimEndTime: number,
  mediaType: GeneratedFromType,
  speed = 1,
  language = AiTranscriptSupportedLanguage.ENGLISH
): Promise<GeneratedAiTranscriptData> => {
  const res = (await window.PMW.pollingAjaxCallAsync(
    generateAiTranscript.bind(null, audioHashedFilename, trimStartTime, trimEndTime, mediaType, speed, language),
    checkAiTranscriptGenerationStatus.bind(null)
  )) as AiTranscriptGenerationResponse;

  if (res.error) {
    throw new Error(res.error);
  }

  if (res.transcript) {
    return res.transcript;
  }

  throw new Error('Neither error, nor transcript received in response!');
};

const checkAiTranscriptGenerationStatus = async (pollingKey: string): Promise<AiTranscriptGenerationResponse> => {
  return (await window.PMW.readLocal('aitranscript/getAiTranscriptGenerationStatus', {
    pollingKey: pollingKey,
  })) as AiTranscriptGenerationResponse;
};

const generateAiTranscript = async (
  audioHashedFilename: string,
  trimStartTime: number,
  trimEndTime: number,
  mediaType: GeneratedFromType,
  speed = 1,
  language = AiTranscriptSupportedLanguage.ENGLISH
): Promise<string> => {
  return (await window.PMW.writeLocal('aitranscript/generateAiTranscriptAndGetSubtitles', {
    hashedFilename: audioHashedFilename,
    startTime: trimStartTime,
    endTime: trimEndTime,
    mediaType,
    speed,
    language,
  })) as Promise<string>;
};

export const replaceExistingTranscriptOnPosterForGeneratedAiTranscript = async (
  existingTranscriptItems: TranscriptItemObject[],
  uidForTranscriptToAdd: string,
  generatedTranscript: GeneratedAiTranscriptData,
  generatedFrom: TranscriptGeneratedFrom,
  selectOnAdd: boolean
): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  if (!existingTranscriptItems.length) {
    return addNewTranscriptToPosterForGeneratedAiTranscript(uidForTranscriptToAdd, generatedTranscript, generatedFrom, selectOnAdd);
  }

  const page = poster.getCurrentPage();
  const subtitles = buildSubtitlesForCurrentPage(generatedTranscript, generatedFrom);

  if (!subtitles.length) {
    return;
  }

  const existingItemOnCurrentPage = existingTranscriptItems.find((item) => poster.getPageForItem(item.uid)?.hashedID === page.hashedID);

  if (existingItemOnCurrentPage) {
    await page.items.replaceItems.replaceTranscript(
      existingItemOnCurrentPage.uid,
      uidForTranscriptToAdd,
      {
        areAIGenerated: true,
        type: ElementDataType.TRANSCRIPT,
        subtitles,
      },
      {
        selectOnAdd,
        optionsToAdd: {generatedFrom},
      }
    );
  } else {
    await page.items.addItems.addTranscriptItem(
      {
        areAIGenerated: true,
        type: ElementDataType.TRANSCRIPT,
        subtitles,
      },
      {
        optionsToAdd: {uid: uidForTranscriptToAdd, generatedFrom},
        selectOnAdd,
      }
    );
  }
};

export const addNewTranscriptToPosterForGeneratedAiTranscript = async (
  uidForTranscriptToAdd: string,
  generatedTranscript: GeneratedAiTranscriptData,
  generatedFrom: TranscriptGeneratedFrom,
  selectOnAdd: boolean
): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const page = poster.getCurrentPage();
  const subtitles = buildSubtitlesForCurrentPage(generatedTranscript, generatedFrom);

  if (!subtitles.length) {
    return;
  }

  await page.items.addItems.addTranscriptItem(
    {
      areAIGenerated: true,
      type: ElementDataType.TRANSCRIPT,
      subtitles,
    },
    {
      optionsToAdd: {uid: uidForTranscriptToAdd, generatedFrom},
      selectOnAdd,
    }
  );
};

/**
 * The transcript the backend returns is one clip-relative, speed-adjusted pass. We
 * place it onto the item's on-page span: the clip replays every `loopPeriod` of
 * on-page time. A background video or audio spans exactly one period, so this emits
 * a single pass; a looping in-page video spans several, so the transcript tiles
 * across them up to the item's onPageEndTime, with the final partial loop clipped.
 */
const buildSubtitlesForCurrentPage = (generatedTranscript: GeneratedAiTranscriptData, generatedFrom: TranscriptGeneratedFrom): AddSubtitleData[] => {
  const subtitles: AddSubtitleData[] = [];

  const loopPeriod = (generatedFrom.endTime - generatedFrom.startTime) / generatedFrom.speed;
  const safeLoopPeriod = loopPeriod > 0 ? loopPeriod : Infinity;

  for (let loopStartTime = generatedFrom.onPageStartTime; loopStartTime < generatedFrom.onPageEndTime - MIN_TIME_GAP_FOR_TRANSCRIPT; loopStartTime += safeLoopPeriod) {
    for (const generatedSubtitle of generatedTranscript.subtitles) {
      const words: AddWordData[] = [];
      for (const word of generatedSubtitle.words) {
        const startTime = word.start + loopStartTime;
        if (startTime >= generatedFrom.onPageEndTime) {
          continue;
        }
        words.push({
          id: getUniqueString(),
          startTime,
          endTime: Math.min(word.end + loopStartTime, generatedFrom.onPageEndTime),
          text: word.text,
        });
      }

      if (!words.length) {
        continue;
      }

      subtitles.push({
        id: getUniqueString(),
        startTime: words[0].startTime,
        endTime: words[words.length - 1].endTime,
        text: words.map((w) => w.text).join(' '),
        words,
      });
    }
  }

  return subtitles;
};

export const showAiSubtitlesUpsellDialog = (): void => {
  void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_SUBTITLES);
};

export const doesTranscriptHaveUserEditedSubtitles = (transcript: TranscriptItemObject): boolean => {
  for (const [, subtitle] of Object.entries(transcript.subtitlesHashmap)) {
    if (subtitle.hasUserEdited) {
      return true;
    }
  }

  return false;
};

export const hasSpeedBeenChangedAfterAiTranscriptGenerationForItems = (itemSpeed: number, transcripts: TranscriptItemObject[]): boolean => {
  if (!transcripts.length) {
    return true;
  }

  return transcripts.some((transcript) => hasSpeedBeenChangedAfterAiTranscriptGeneration(itemSpeed, transcript.generatedFrom?.speed));
};

export const hasSpeedBeenChangedAfterAiTranscriptGeneration = (itemSpeed: number, transcriptGeneratedFromSpeed?: number): boolean => {
  if (!transcriptGeneratedFromSpeed) {
    return true;
  }

  return itemSpeed !== transcriptGeneratedFromSpeed;
};

export const getAiTranscriptParamsForAudio = (audioItem: AudioItemObject): AiTranscriptForItemWithParams => {
  const onPageDuration = getTrimmedPlaybackDuration(audioItem.audioPlayer);
  return {
    uid: audioItem.uid,
    type: 'audio',
    hashedFilename: audioItem.hashedFilename,
    startTime: getTrimmedStartTime(audioItem.audioPlayer),
    endTime: getTrimmedEndTime(audioItem.audioPlayer),
    onPageStartTime: audioItem.onPageStartTime,
    onPageEndTime: audioItem.onPageStartTime + onPageDuration,
    speed: audioItem.audioPlayer.speed,
  };
};

/**
 * 1-based index of a video item within the ordered list of video items on a page.
 * Used as a display label ("Video 1", "Video 2", ...) in the AI subtitles panel.
 * Returns -1 when the videoUid is not found on the page.
 */
export const getVideoItemOrderInPage = (videoUid: string, currentPage: Page): number => {
  const itemIdsInOrder = currentPage.items.getItemIdsInOrder();
  const videoItemIdsInOrder = itemIdsInOrder.filter((itemId) => currentPage.items.itemsHashMap[itemId].isVideo());
  const idx = videoItemIdsInOrder.indexOf(videoUid);
  if (idx === -1) {
    return -1;
  }
  return idx + 1;
};

export const getAiTranscriptParamsForVideo = (videoItem: VideoItemObject): AiTranscriptForItemWithParams => {
  return {
    uid: videoItem.uid,
    type: 'video',
    hashedFilename: videoItem.hashedFilename,
    startTime: videoItem.startTime,
    endTime: videoItem.endTime,
    onPageStartTime: videoItem.onPageStartTime,
    onPageEndTime: videoItem.onPageEndTime,
    speed: videoItem.speed ?? 1,
  };
};

export const getSubtitlesWordArrayAfterTextEdited = (editedText: string, subtitleStartTime: number, subtitleEndTime: number): WordObject[] => {
  const wordsToMake = editedText.trim().split(' ');
  const numberOfWords = wordsToMake.length;
  const durationOfEachWord = (subtitleEndTime - subtitleStartTime) / numberOfWords;

  const newWords: WordObject[] = [];

  let startTime = subtitleStartTime;

  for (let i = 0; i < numberOfWords; i++) {
    const cleanWord = removePunctuationFromWord(wordsToMake[i]);

    newWords.push({
      id: getUniqueString(),
      text: cleanWord,
      startTime,
      endTime: startTime + durationOfEachWord,
    });

    startTime = startTime + durationOfEachWord + 0.01;
  }

  return newWords;
};

export const addNewManualTranscript = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const currentPage = poster.getCurrentPage();

  const subtitles: AddSubtitleData[] = [
    {
      id: getUniqueString(),
      startTime: 0,
      endTime: 3,
      text: 'The quick brown fox',
      words: [
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'The',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'quick',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'brown',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'fox',
        },
      ],
    },
    {
      id: getUniqueString(),
      startTime: 3 + MIN_TIME_GAP_FOR_TRANSCRIPT,
      endTime: 5,
      text: 'jumps over the lazy dog',
      words: [
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'jumps',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'over',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'the',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'lazy',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'dog',
        },
      ],
    },
  ];

  await currentPage.items.addItems.addTranscriptItem(
    {
      areAIGenerated: false,
      type: ElementDataType.TRANSCRIPT,
      subtitles,
    },
    {
      optionsToAdd: {},
      selectOnAdd: true,
    }
  );
};

const getNameForVideoItem = (videoOrder = 0): string => {
  return `Video ${videoOrder}`;
};

export const getSelectableItemDataForVideoData = (item: VideoItemObject, videoOrder: number): SelectableItemWithAudio => {
  return {
    duration: item.endTime - item.startTime,
    name: getNameForVideoItem(videoOrder),
    imageUrl: repoVideoThumbImageURL(item.hashedFilename, item.fileExtension),
    id: item.uid,
    isSelected: false,
    type: 'video',
    sourceProperties: getAiTranscriptParamsForVideo(item),
  };
};

export const getSelectableItemDataForAudioData = (item: AudioItemObject): SelectableItemWithAudio => {
  return {
    duration: getTrimmedDuration(item.audioPlayer),
    name: item.name,
    imageUrl: getRandomThumbnailImageForAudioItem(),
    id: item.uid,
    isSelected: false,
    type: 'audio',
    sourceProperties: getAiTranscriptParamsForAudio(item),
  };
};

export const getAiSubtitlesPanelHeightClass = (aiSubtitleState: AiSubtitleState, itemsCount: number, generationsCount: number, isPremium = false): string => {
  if (aiSubtitleState === AiSubtitleState.IDLE) {
    const numberOfItems = itemsCount;
    const baseStyle = numberOfItems <= 3 ? styles.aiSubtitlesChooseMediaLevel1 : numberOfItems <= 7 ? styles.aiSubtitlesChooseMediaLevel2 : styles.aiSubtitlesChooseMediaLevel3;

    return `${baseStyle} ${isPremium ? styles.premium : ''}`;
  }

  const numberOfItemsGenerating = generationsCount;
  if (numberOfItemsGenerating === 1) {
    return styles.aiSubtitlesGeneratingLevel1;
  }

  if (numberOfItemsGenerating === 2) {
    return styles.aiSubtitlesGeneratingLevel2;
  }

  if (numberOfItemsGenerating === 3) {
    return styles.aiSubtitlesGeneratingLevel3;
  }

  if (numberOfItemsGenerating === 4) {
    return styles.aiSubtitlesGeneratingLevel4;
  }

  return styles.aiSubtitlesGeneratingLevel5;
};

export const deepCloneSubtitles = (subtitles: PosterEditorSubtitles): PosterEditorSubtitles => {
  const clonedHashmap: Record<string, SubtitleObject> = {};
  for (const [key, value] of Object.entries(subtitles.subtitlesHashmap)) {
    clonedHashmap[key] = {
      ...value,
      words: value.words.map((w) => ({...w})),
    };
  }
  return {
    subtitlesOrder: [...subtitles.subtitlesOrder],
    subtitlesHashmap: clonedHashmap,
  };
};
